import React, { useRef, useEffect } from 'react';
import { Filter } from 'lucide-react';
import { FilterPopover } from './FilterPopover';
import { useMultiFilter } from '../../hooks/useMultiFilter';

interface FilterField {
  id: string;
  name: string;
  type?: string;
}

interface FilterButtonProps {
  fields: FilterField[];
  onFilterChange?: (conditions: any[]) => void;
}

export const FilterButton: React.FC<FilterButtonProps> = ({ fields, onFilterChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const onFilterChangeRef = useRef(onFilterChange);

  // Keep ref updated
  useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
  }, [onFilterChange]);

  const {
    state,
    addCondition,
    removeCondition,
    updateCondition,
    reorderConditions,
    clear
  } = useMultiFilter();

  // Notify parent whenever conditions change
  useEffect(() => {
    if (onFilterChangeRef.current) {
      // Filter out invalid conditions (no fieldId or error)
      const validConditions = state.conditions.filter(
        c => c.fieldId && !c.error
      );
      onFilterChangeRef.current(validConditions);
    }
  }, [state.conditions]);

  const activeCount = state.conditions.filter(c => c.fieldId && !c.error).length;
  const isActive = activeCount > 0;

  const handleOpen = () => {
    // If no conditions exist, add one automatically
    if (state.conditions.length === 0) {
      addCondition();
    }
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleClear = () => {
    clear();
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleOpen}
        aria-expanded={isOpen}
        aria-controls="filter-panel"
        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium rounded-lg transition-all outline-none ${
          isActive
            ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
        }`}
      >
        <Filter className="w-3.5 h-3.5" />
        <span>{isActive ? `Filter (${activeCount})` : 'Filter'}</span>
      </button>

      <FilterPopover
        isOpen={isOpen}
        onClose={handleClose}
        anchorEl={buttonRef.current}
        fields={fields}
        conditions={state.conditions}
        onAddCondition={() => addCondition()}
        onRemoveCondition={removeCondition}
        onUpdateCondition={updateCondition}
        onReorderConditions={reorderConditions}
        onClear={handleClear}
      />
    </>
  );
};
